<template>
    <FormSection title="Clinical History" :full-height="false" :application-id="application">
        <template #content>
            <section class="p-2 pb-0 g-0 row">
                <div class="col-12 col-md-6 g-0">
                    <div class="offset-md-1 form-group">
                        <label for="medical-history"> Past Medical History </label>
                        <PastDiagnosisHistory v-model:diagnosis="patientDetails.healthHistory.pastMedicalHistories" />
                        <div class="mt-3">
                            <label for="resolved-problems"> Resolved Problems </label>
                            <ProblemsTable v-model:problems="patientDetails.healthHistory.resolvedProblems" />
                            <label for="past-medical-comments"> Comments </label>
                        </div>
                        <CommentInput
                            input-name="past-medical-comments"
                            placeholder="Enter Comments"
                            :result="patientDetails.healthHistory.pastMedicalHistoryComment"
                            v-on:resultUpdatedAppended="medicalResultUpdatedAppended"
                            v-on:resultUpdated="medicalResultUpdated"
                            :patient-id="patientId"
                            :max-length="5000"
                            :application="application"
                        />
                    </div>
                    <div class="offset-md-1 form-group">
                        <div class="d-flex justify-content-between">
                            <label for="labs"> Labs </label>
                            <button class="btn btn-xs btn-outline-app-secondary" @click="manageDocuments()">
                                Manage Documents
                            </button>
                        </div>
                        <p>This table contains documents labeled labs. To upload new labs, click Manage Documents.</p>
                        <PatientDocuments
                            :patient-id="patientId"
                            :delete-document="deleteDocument"
                            :get-documents="getLabsDocuments"
                            :is-history-page="true"
                            :comment-document="commentDocument"
                            :is-loading-comment="isLoadingComment"
                            :provider-id="patientOptions.data.providerId"
                            :application="application"
                            :accountId="accountId"
                            documentType="Labs"
                        />

                        <div class="form-group">
                            <label for="scans-tests"> Scans/Tests </label>
                            <p>
                                This table contains documents labeled scans/tests. To upload new scans/tests, click
                                Manage Documents.
                            </p>
                            <PatientDocuments
                                :patient-id="patientId"
                                :delete-document="deleteDocument"
                                :get-documents="getScansDocuments"
                                :is-history-page="true"
                                :comment-document="commentDocument"
                                :is-loading-comment="isLoadingComment"
                                :provider-id="patientOptions.data.providerId"
                                :application="application"
                                :accountId="accountId"
                                documentType="Scans/Tests"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 g-0 form-group">
                    <div
                        class="offset-md-1"
                        v-for="(history, index) of patientDetails.healthHistory.patientClinicalHistories"
                    >
                        <label :for="`surgical-history-${history.type}`">
                            {{ historyType.getDescriptionUsingValue(history.type) }}
                        </label>
                        <span v-if="isLoading" class="shimmer-loading"></span>
                        <CommentInput
                            v-else
                            :input-name="`surgical-history-${history.type}`"
                            :placeholder="`Enter ${historyType.getDescriptionUsingValue(history.type)}`"
                            :result="history.description"
                            :patient-id="patientId"
                            v-on:resultUpdatedAppended="(value:string)=>resultUpdatedAppended(value,index)"
                            v-on:resultUpdated="(value:string)=>resultUpdated(value,index)"
                            :max-length="5000"
                            :application="application"
                        />
                    </div>
                    <HealthHistorySection :patient-health-history="patientDetails.healthHistory" />
                </div>
                <!-- PEDIATRIC HISTORY -->
                <div class="col-md-12 g-0">
                    <div class="form-check custom-checkbox form-check-inline me-3">
                        <input
                            type="checkbox"
                            id="pediatric-visit"
                            class="form-check-input"
                            v-model="patientDetails.healthHistory.showPediatricHistory"
                            :disabled="isLoading"
                            @change="handleShowPediatricHistory()"
                        />
                        <label class="form-check-label" for="pediatric-visit">
                            <span class="me-1">Show Pediatric History Sections</span>
                        </label>
                    </div>
                    <PediatricHistoryForm
                        v-if="patientDetails.healthHistory.showPediatricHistory"
                        :patientHealthHistory="patientDetails.healthHistory"
                        :isWeightImperial="isWeightImperial"
                        :isLengthImperial="isLengthImperial"
                        :isCircumferenceImperial="isCircumferenceImperial"
                        :birthWeightLarge="birthWeightLarge"
                        :birthWeightSmall="birthWeightSmall"
                    />
                </div>
                <!-- FAMILY HEALTH HISTORY -->
                <div class="col-12">
                    <FamilyHealthHistory :patient-id="patientId" :accountId="accountId" :application="application" />
                </div>
                <!-- HOSPITALIZATIONS -->
                <div class="col-12">
                    <HospitalizationsTable
                        :patient-id="patientId"
                        :provider-id="patientOptions.data.providerId"
                        :accountId="accountId"
                    />
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ClinicalHistorySection.ts" />
